import { useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  Link,
  Stack,
  Typography,
} from '@mui/material';
import AutoStoriesIcon from '@mui/icons-material/AutoStories';
import PublicIcon from '@mui/icons-material/Public';
import PeopleIcon from '@mui/icons-material/People';
import EditNoteIcon from '@mui/icons-material/EditNote';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import { useNavigate } from '@tanstack/react-router';

interface OnboardingTourProps {
  open: boolean;
  onComplete: () => void;
}

interface TourStep {
  icon: React.ReactNode;
  title: string;
  text: string;
  cta?: string;
  ctaRoute?: string;
}

const STEPS: TourStep[] = [
  {
    icon: <AutoStoriesIcon sx={{ fontSize: 64, color: 'primary.main' }} />,
    title: 'Welcome to Immersive Books',
    text: 'Your creative studio for building illustrated, narrated books with AI. Six powerful engines work together to help you create immersive reading experiences.',
  },
  {
    icon: <PublicIcon sx={{ fontSize: 64, color: 'primary.main' }} />,
    title: 'Build Your World',
    text: 'Start with the World Builder to define your setting — magic systems, locations, cultures, and more. Or jump straight to writing.',
    cta: 'Go to Worlds',
    ctaRoute: '/worlds',
  },
  {
    icon: <PeopleIcon sx={{ fontSize: 64, color: 'primary.main' }} />,
    title: 'Create Characters',
    text: 'Build detailed character profiles with visual descriptors, voice profiles, and AI-generated portraits. Characters appear consistently across your book.',
    cta: 'Go to Entities',
    ctaRoute: '/entities',
  },
  {
    icon: <EditNoteIcon sx={{ fontSize: 64, color: 'primary.main' }} />,
    title: 'Write with AI',
    text: 'Use Story Studio to draft with AI assistance, or write directly in the Book Editor. Generate images and narration for every paragraph.',
    cta: 'Go to Story Studio',
    ctaRoute: '/story-studio',
  },
  {
    icon: <AutoFixHighIcon sx={{ fontSize: 64, color: 'primary.main' }} />,
    title: 'AI Everywhere',
    text: 'Right-click any paragraph, use \u2318/ for slash commands, or click the AI button in the toolbar. Generate images, audio, expand text, rewrite — AI is available on every surface.',
    cta: 'Get Started',
  },
];

export function OnboardingTour({ open, onComplete }: OnboardingTourProps) {
  const [step, setStep] = useState(0);
  const navigate = useNavigate();
  const current = STEPS[step];
  const isLast = step === STEPS.length - 1;

  const handleComplete = () => {
    try {
      localStorage.setItem('onboarding_completed', 'true');
    } catch {
      /* ignore */
    }
    onComplete();
  };

  const handleCtaClick = () => {
    if (isLast) {
      handleComplete();
      return;
    }
    if (current.ctaRoute) {
      handleComplete();
      navigate({ to: current.ctaRoute });
    }
  };

  const handleNext = () => {
    if (isLast) {
      handleComplete();
    } else {
      setStep((s) => s + 1);
    }
  };

  const handleBack = () => {
    setStep((s) => Math.max(0, s - 1));
  };

  return (
    <Dialog open={open} maxWidth="sm" fullWidth>
      <DialogContent>
        <Stack
          alignItems="center"
          spacing={3}
          sx={{ py: 3, textAlign: 'center' }}
        >
          {current.icon}
          <Typography variant="h5" fontWeight="bold">
            {current.title}
          </Typography>
          <Typography
            variant="body1"
            color="text.secondary"
            sx={{ maxWidth: 400 }}
          >
            {current.text}
          </Typography>
          {current.cta && (
            <Button variant="outlined" onClick={handleCtaClick}>
              {current.cta} {!isLast && '\u2192'}
            </Button>
          )}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ justifyContent: 'space-between', px: 3, pb: 2 }}>
        <Link
          component="button"
          variant="body2"
          onClick={handleComplete}
          underline="hover"
          color="text.secondary"
          sx={{ cursor: 'pointer' }}
        >
          Skip tour
        </Link>
        <Stack direction="row" alignItems="center" spacing={2}>
          {/* Step indicator dots */}
          <Stack direction="row" spacing={0.5}>
            {STEPS.map((_, i) => (
              <Box
                key={i}
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  bgcolor: i === step ? 'primary.main' : 'grey.400',
                  transition: 'background-color 0.2s',
                }}
              />
            ))}
          </Stack>
          <Stack direction="row" spacing={1}>
            <Button onClick={handleBack} disabled={step === 0}>
              Back
            </Button>
            <Button variant="contained" onClick={handleNext}>
              {isLast ? 'Get Started' : 'Next'}
            </Button>
          </Stack>
        </Stack>
      </DialogActions>
    </Dialog>
  );
}
